/**
 * NOVAPAY-116-R5C — Telegram-scoped local customer profile / checkout prefill.
 * Reuses the existing checkout-prefill SoT key family; never crosses Telegram users.
 */

import type { NovaPoshtaReferenceSelection } from "./nova-poshta-reference-v1.js";

export interface CustomerProfileV2 {
  name: string;
  surname: string;
  phone: string;
  city: string;
  warehouse: string;
  note?: string;
  /** COMMERCE-176C-R1 — optional birthday (YYYY-MM-DD). */
  birthday?: string;
  /** COMMERCE-176D-R1 */
  email?: string;
  telegram?: string;
  /** COMMERCE-176D-R1 recovery — optional Viber phone preference (no Business send claim). */
  viber?: string;
  preferred_contact?: "telegram" | "phone" | "email" | "viber";
  locale?: string;
  updated_at?: string;
  /** NOVA-POSHTA-117A-R1 — last validated NP selection */
  np_selection?: NovaPoshtaReferenceSelection;
  /** COMMERCE-CANARY-163A-R2 — name priority source marker. */
  name_source?: "user" | "telegram" | "profile";
}

const PREFILL_KEY = "asiastyle.store-v2.checkout-prefill.v1";
const CONSENT_STORE = "asiastyle.store-v2.consent.v1";
const IDENTITY_SCOPE_STORE = "asiastyle.store-v2.identity-scope.v1";

function currentEnvironmentScope(): string {
  try {
    return String(window.location.host || "unknown_host").trim().toLowerCase();
  } catch {
    return "unknown_host";
  }
}

function currentBotScope(): string {
  try {
    const support = String(
      (import.meta as unknown as { env?: Record<string, string | undefined> }).env?.VITE_SUPPORT_TELEGRAM_URL ??
        "",
    ).trim();
    const m = support.match(/t\.me\/(@?[\w_]+)/i);
    if (m?.[1]) return m[1].replace(/^@/, "").toLowerCase();
  } catch {
    /* ignore */
  }
  return "asiastylestagingtestbot";
}

export function currentIdentityScope(): string {
  const tg = telegramUserId() ?? "anon";
  return `${currentEnvironmentScope()}:${currentBotScope()}:${tg}`;
}

export function telegramUserId(): string | null {
  try {
    const id = (
      window as unknown as {
        Telegram?: { WebApp?: { initDataUnsafe?: { user?: { id?: number | string } } } };
      }
    ).Telegram?.WebApp?.initDataUnsafe?.user?.id;
    if (id != null && id !== "") return String(id);
  } catch {
    /* ignore */
  }
  try {
    const staging =
      String((import.meta as unknown as { env?: Record<string, string> }).env?.VITE_STAGING_DIAGNOSTICS ?? "") === "1";
    if (!staging) return null;
    const q = new URLSearchParams(window.location.search).get("owner_tg");
    if (q && /^\d+$/.test(q)) return q;
  } catch {
    /* ignore */
  }
  return null;
}

/** COMMERCE-176D-R1 — Telegram WebApp user fields for profile prefill. */
export function telegramWebAppUser(): {
  id: string | null;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
} {
  try {
    const user = (
      window as unknown as {
        Telegram?: {
          WebApp?: {
            initDataUnsafe?: {
              user?: {
                id?: number | string;
                username?: string;
                first_name?: string;
                last_name?: string;
              };
            };
          };
        };
      }
    ).Telegram?.WebApp?.initDataUnsafe?.user;
    if (!user) return { id: null, username: null, firstName: null, lastName: null };
    return {
      id: user.id == null || user.id === "" ? null : String(user.id),
      username: user.username ? String(user.username).replace(/^@/, "") : null,
      firstName: user.first_name ? String(user.first_name) : null,
      lastName: user.last_name ? String(user.last_name) : null,
    };
  } catch {
    return { id: null, username: null, firstName: null, lastName: null };
  }
}

function scopedKey(base: string): string {
  const scope = currentIdentityScope();
  return `${base}.scope.${scope}`;
}

function readRaw(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeRaw(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

export function readCheckoutPrefill(): CustomerProfileV2 | null {
  const key = scopedKey(PREFILL_KEY);
  const raw = readRaw(key) ?? readRaw(PREFILL_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<CustomerProfileV2> & {
      telegram_user_id?: string | number | null;
      identity_scope?: string | null;
      name_source?: string | null;
    };
    const currentTg = telegramUserId();
    const parsedTg = parsed.telegram_user_id == null ? null : String(parsed.telegram_user_id);
    // Never reuse cached profile of another Telegram user.
    if (currentTg && parsedTg && currentTg !== parsedTg) return null;
    const parsedScope = String(parsed.identity_scope ?? "").trim();
    if (parsedScope && parsedScope !== currentIdentityScope()) return null;
    const out: CustomerProfileV2 = {
      name: String(parsed.name ?? "").slice(0, 80),
      surname: String(parsed.surname ?? "").slice(0, 80),
      phone: String(parsed.phone ?? "").slice(0, 20),
      city: String(parsed.city ?? "").slice(0, 120),
      warehouse: String(parsed.warehouse ?? "").slice(0, 120),
      note: String(parsed.note ?? "").slice(0, 300),
    };
    if (parsed.birthday) out.birthday = String(parsed.birthday).slice(0, 10);
    if (parsed.email) out.email = String(parsed.email).slice(0, 120);
    if (parsed.telegram) out.telegram = String(parsed.telegram).slice(0, 64);
    if (parsed.viber) out.viber = String(parsed.viber).slice(0, 32);
    if (
      parsed.preferred_contact === "telegram" ||
      parsed.preferred_contact === "phone" ||
      parsed.preferred_contact === "email" ||
      parsed.preferred_contact === "viber"
    ) {
      out.preferred_contact = parsed.preferred_contact;
    }
    if (parsed.locale) out.locale = String(parsed.locale).slice(0, 8);
    if (parsed.updated_at) out.updated_at = String(parsed.updated_at);
    if (parsed.np_selection && typeof parsed.np_selection === "object") {
      out.np_selection = parsed.np_selection as NovaPoshtaReferenceSelection;
    }
    if (parsed.name_source === "user" || parsed.name_source === "telegram" || parsed.name_source === "profile") {
      out.name_source = parsed.name_source;
    }
    return out;
  } catch {
    return null;
  }
}

/** Persist only after explicit confirm / save — not draft keystrokes. */
export function writeCheckoutPrefill(
  data: CustomerProfileV2 & { name_source?: "user" | "telegram" | "profile" },
): void {
  const tg = telegramUserId();
  const existing = readCheckoutPrefill();
  const requested = data.name_source;
  // Never let Telegram overwrite an already-saved user/profile name.
  let nameSource: "user" | "telegram" | "profile" = "user";
  if (requested === "telegram") {
    if (existing?.name_source === "user" || existing?.name_source === "profile") {
      nameSource = existing.name_source;
    } else {
      nameSource = "telegram";
    }
  } else if (requested === "profile") {
    nameSource = "profile";
  } else if (existing?.name_source === "user" || existing?.name_source === "profile") {
    nameSource = existing.name_source;
  } else {
    nameSource = "user";
  }
  const keepExistingName =
    requested === "telegram" &&
    (existing?.name_source === "user" || existing?.name_source === "profile") &&
    Boolean(existing?.name?.trim());
  const payloadObj: Record<string, unknown> = {
    name: (keepExistingName ? existing!.name : data.name).slice(0, 80),
    surname: (keepExistingName && existing?.surname ? existing.surname : data.surname).slice(0, 80),
    phone: data.phone.slice(0, 20),
    city: data.city.slice(0, 120),
    warehouse: data.warehouse.slice(0, 120),
    note: String(data.note ?? "").slice(0, 300),
    updated_at: new Date().toISOString(),
    telegram_user_id: tg,
    identity_scope: currentIdentityScope(),
    name_source: nameSource,
  };
  if (data.locale) payloadObj.locale = String(data.locale).slice(0, 8);
  if (data.birthday) payloadObj.birthday = String(data.birthday).slice(0, 10);
  if (data.email) payloadObj.email = String(data.email).slice(0, 120);
  if (data.telegram) payloadObj.telegram = String(data.telegram).slice(0, 64);
  if (data.viber) payloadObj.viber = String(data.viber).slice(0, 32);
  if (data.preferred_contact) payloadObj.preferred_contact = data.preferred_contact;
  if (data.np_selection) payloadObj.np_selection = data.np_selection;
  const payload = JSON.stringify(payloadObj);
  writeRaw(scopedKey(PREFILL_KEY), payload);
  // Keep legacy key for same-device non-TG browsers without leaking cross-user when TG id exists.
  if (!tg) writeRaw(PREFILL_KEY, payload);
}

/** R2-R6 / COMMERCE-175A — Telegram-scoped delivery address book (local; order snapshots stay immutable). */
export type CustomerAddressDeliveryType =
  | "NOVA_POSHTA_BRANCH"
  | "NOVA_POSHTA_PARCEL_LOCKER"
  | "NOVA_POSHTA_COURIER";

export type CustomerAddressSource = "NOVA_POSHTA_DIRECTORY" | "MANUAL";

export interface CustomerSavedAddressV2 {
  id: string;
  label: string;
  name: string;
  surname: string;
  phone: string;
  city: string;
  warehouse: string;
  is_default: boolean;
  updated_at: string;
  delivery_type?: CustomerAddressDeliveryType;
  branch_ref?: string;
  branch_number?: string;
  locker_ref?: string;
  locker_number?: string;
  settlement_ref?: string;
  street?: string;
  building?: string;
  apartment?: string;
  entrance?: string;
  floor?: string;
  intercom?: string;
  comment?: string;
  display_address?: string;
  archived?: boolean;
  /** COMMERCE-176A — directory vs exceptional manual entry */
  address_source?: CustomerAddressSource;
  /** Canonical NP selection snapshot for checkout prefill */
  np_selection?: NovaPoshtaReferenceSelection;
}

const ADDRESS_BOOK_KEY = "asiastyle.store-v2.address-book.v1";

export function readAddressBook(): CustomerSavedAddressV2[] {
  const key = scopedKey(ADDRESS_BOOK_KEY);
  const raw = readRaw(key);
  if (!raw) {
    // Seed from checkout prefill when present (same Telegram user only).
    const prefill = readCheckoutPrefill();
    if (!prefill?.city && !prefill?.warehouse) return [];
    const seeded: CustomerSavedAddressV2 = {
      id: "prefill-default",
      label: "Default",
      name: prefill.name,
      surname: prefill.surname,
      phone: prefill.phone,
      city: prefill.city,
      warehouse: prefill.warehouse,
      is_default: true,
      updated_at: prefill.updated_at ?? new Date().toISOString(),
    };
    return [seeded];
  }
  try {
    const parsed = JSON.parse(raw) as { addresses?: CustomerSavedAddressV2[] };
    const list = Array.isArray(parsed.addresses) ? parsed.addresses : [];
    return list
      .map((a) => {
        const out: CustomerSavedAddressV2 = {
          id: String(a.id ?? "").slice(0, 40),
          label: String(a.label ?? "Address").slice(0, 40),
          name: String(a.name ?? "").slice(0, 80),
          surname: String(a.surname ?? "").slice(0, 80),
          phone: String(a.phone ?? "").slice(0, 20),
          city: String(a.city ?? "").slice(0, 120),
          warehouse: String(a.warehouse ?? "").slice(0, 120),
          is_default: Boolean(a.is_default),
          updated_at: String(a.updated_at ?? ""),
          delivery_type: (["NOVA_POSHTA_BRANCH", "NOVA_POSHTA_PARCEL_LOCKER", "NOVA_POSHTA_COURIER"].includes(
            String(a.delivery_type ?? ""),
          )
            ? (a.delivery_type as CustomerAddressDeliveryType)
            : "NOVA_POSHTA_BRANCH"),
        };
        if (a.branch_ref) out.branch_ref = String(a.branch_ref).slice(0, 80);
        if (a.branch_number) out.branch_number = String(a.branch_number).slice(0, 40);
        if (a.locker_ref) out.locker_ref = String(a.locker_ref).slice(0, 80);
        if (a.locker_number) out.locker_number = String(a.locker_number).slice(0, 40);
        if (a.street) out.street = String(a.street).slice(0, 120);
        if (a.building) out.building = String(a.building).slice(0, 40);
        if (a.apartment) out.apartment = String(a.apartment).slice(0, 40);
        if (a.comment) out.comment = String(a.comment).slice(0, 200);
        if (a.display_address) out.display_address = String(a.display_address).slice(0, 200);
        if (a.archived) out.archived = true;
        if (a.settlement_ref) out.settlement_ref = String(a.settlement_ref).slice(0, 80);
        if (a.entrance) out.entrance = String(a.entrance).slice(0, 20);
        if (a.floor) out.floor = String(a.floor).slice(0, 20);
        if (a.intercom) out.intercom = String(a.intercom).slice(0, 40);
        if (a.address_source === "MANUAL" || a.address_source === "NOVA_POSHTA_DIRECTORY") {
          out.address_source = a.address_source;
        }
        if (a.np_selection && typeof a.np_selection === "object") {
          out.np_selection = a.np_selection as NovaPoshtaReferenceSelection;
        }
        return out;
      })
      .filter((a) => a.id && !a.archived && (a.city || a.warehouse || a.phone || a.street));
  } catch {
    return [];
  }
}

export function writeAddressBook(addresses: CustomerSavedAddressV2[]): void {
  const capped = addresses.slice(0, 8).map((a) => ({
    ...a,
    updated_at: a.updated_at || new Date().toISOString(),
  }));
  writeRaw(scopedKey(ADDRESS_BOOK_KEY), JSON.stringify({ addresses: capped }));
}

export function upsertSavedAddress(input: Omit<CustomerSavedAddressV2, "updated_at"> & { updated_at?: string }): CustomerSavedAddressV2[] {
  const list = readAddressBook().filter((a) => a.id !== "prefill-default" || input.id === "prefill-default");
  const next: CustomerSavedAddressV2 = {
    ...input,
    updated_at: input.updated_at ?? new Date().toISOString(),
  };
  const without = list.filter((a) => a.id !== next.id);
  const merged = next.is_default
    ? [next, ...without.map((a) => ({ ...a, is_default: false }))]
    : [...without, next];
  if (!merged.some((a) => a.is_default) && merged[0]) merged[0].is_default = true;
  writeAddressBook(merged);
  const def = merged.find((a) => a.is_default) ?? merged[0];
  if (def) {
    writeCheckoutPrefill({
      name: def.name,
      surname: def.surname,
      phone: def.phone,
      city: def.city,
      warehouse: def.warehouse,
      ...(def.comment ? { note: def.comment } : {}),
      ...(def.np_selection ? { np_selection: def.np_selection } : {}),
    });
  }
  return merged;
}

export function deleteSavedAddress(id: string): CustomerSavedAddressV2[] {
  const next = readAddressBook().filter((a) => a.id !== id);
  if (next.length && !next.some((a) => a.is_default)) next[0]!.is_default = true;
  writeAddressBook(next);
  return next;
}

export function archiveSavedAddress(id: string): CustomerSavedAddressV2[] {
  const next = readAddressBook().map((a) =>
    a.id === id ? { ...a, archived: true, is_default: false, updated_at: new Date().toISOString() } : a,
  );
  const active = next.filter((a) => !a.archived);
  if (active.length && !active.some((a) => a.is_default)) active[0]!.is_default = true;
  writeAddressBook(next);
  return active;
}

export function setDefaultSavedAddress(id: string): CustomerSavedAddressV2[] {
  const next = readAddressBook().map((a) => ({ ...a, is_default: a.id === id }));
  writeAddressBook(next);
  const def = next.find((a) => a.is_default);
  if (def) {
    writeCheckoutPrefill({
      name: def.name,
      surname: def.surname,
      phone: def.phone,
      city: def.city,
      warehouse: def.warehouse,
      ...(def.comment ? { note: def.comment } : {}),
      ...(def.np_selection ? { np_selection: def.np_selection } : {}),
    });
  }
  return next;
}

/** COMMERCE-176A — primary saved delivery profile for checkout prefill. */
export function getPrimaryDeliveryProfile(): CustomerSavedAddressV2 | null {
  const list = readAddressBook();
  if (!list.length) return null;
  return list.find((a) => a.is_default) ?? list[0] ?? null;
}

export function readConsentAccepted(consentVersion: string): boolean {
  const key = scopedKey(CONSENT_STORE);
  const raw = readRaw(key) ?? readRaw(CONSENT_STORE);
  if (!raw) return false;
  try {
    if (raw === consentVersion) return true;
    const saved = JSON.parse(raw) as { version?: string };
    return saved?.version === consentVersion;
  } catch {
    return false;
  }
}

/** Customer-facing timestamp only — never expose raw ids. */
export function readConsentAcceptedAt(consentVersion: string): string | null {
  const key = scopedKey(CONSENT_STORE);
  const raw = readRaw(key) ?? readRaw(CONSENT_STORE);
  if (!raw) return null;
  try {
    const saved = JSON.parse(raw) as { version?: string; accepted_at?: string };
    if (saved?.version !== consentVersion) return null;
    const at = String(saved.accepted_at ?? "").trim();
    return at || null;
  } catch {
    return null;
  }
}

export function persistConsent(input: {
  version: string;
  locale: string;
  tenant?: string;
}): void {
  const payload = JSON.stringify({
    version: input.version,
    locale: input.locale,
    tenant: input.tenant ?? "asiastyle",
    telegram_user_id: telegramUserId(),
    accepted_at: new Date().toISOString(),
  });
  writeRaw(scopedKey(CONSENT_STORE), payload);
  if (!telegramUserId()) writeRaw(CONSENT_STORE, payload);
}

/** Staging first-run — clear mutable onboarding/profile only. Never touches orders. */
export function clearMutableFirstRunCustomerState(): void {
  try {
    const prefixes = [
      PREFILL_KEY,
      "asiastyle.store-v2.address-book.v1",
      CONSENT_STORE,
      "asiastyle.store-v2.cart.v1",
      "asiastyle.store-v2.promo.v1",
      "asiastyle.store-v2.np-checkout-draft.v1",
      "asiastyle.store-v2.checkout-attempt.v1",
      "asiastyle.store-v2.payment-resume",
    ];
    const removeMatching = (storage: Storage) => {
      const toRemove: string[] = [];
      for (let i = 0; i < storage.length; i += 1) {
        const k = storage.key(i);
        if (!k) continue;
        if (prefixes.some((p) => k === p || k.startsWith(`${p}:`) || k.startsWith(p))) toRemove.push(k);
      }
      for (const k of toRemove) storage.removeItem(k);
    };
    removeMatching(window.localStorage);
    removeMatching(window.sessionStorage);
  } catch {
    /* ignore */
  }
}

/** Staging QA / version bump — clears only consent preference keys. */
export function clearConsentPreferenceOnly(): void {
  try {
    window.localStorage.removeItem(scopedKey(CONSENT_STORE));
    window.localStorage.removeItem(CONSENT_STORE);
  } catch {
    /* ignore */
  }
}

/**
 * COMMERCE-CANARY-163A-R2 — isolate customer-bound caches by environment+bot+telegram user.
 * On identity scope change we clear customer-bound stale caches to prevent cross-user leaks.
 */
export function ensureIdentityScopedCaches(): void {
  try {
    const nextScope = currentIdentityScope();
    const prevScope = window.localStorage.getItem(IDENTITY_SCOPE_STORE);
    if (!prevScope || prevScope === nextScope) {
      window.localStorage.setItem(IDENTITY_SCOPE_STORE, nextScope);
      return;
    }
    const removePrefixes = [
      "asiastyle.store-v2.checkout-prefill.v1",
      "asiastyle.store-v2.address-book.v1",
      "asiastyle.store-v2.consent.v1",
      "asiastyle.store-v2.session.v1",
      "asiastyle.store-v2.cart.v1",
      "asiastyle.store-v2.promo.v1",
      "asiastyle.store-v2.np-checkout-draft.v1",
    ];
    const toRemove: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const k = window.localStorage.key(i);
      if (!k) continue;
      if (removePrefixes.some((p) => k.startsWith(p))) toRemove.push(k);
    }
    for (const k of toRemove) window.localStorage.removeItem(k);
    window.localStorage.setItem(IDENTITY_SCOPE_STORE, nextScope);
  } catch {
    /* ignore */
  }
}
